import React from 'react';
import { motion } from 'framer-motion';
import {
  formatDelta,
  getImpactTone,
  getModuleDetailLines,
  getModuleFailedChecks,
  getScoreTone
} from '../utils/formatters';
import GradeRing from './GradeRing';
import { buildStaticMapUrl } from '../api/graderApi';

const EASE = [0.22, 1, 0.36, 1];

const ordinal = (n) => {
  const v = Math.round(Number(n) || 0);
  const m = v % 100;
  const s = ['th', 'st', 'nd', 'rd'];
  return `${v}${s[(m - 20) % 10] || s[m] || s[0]}`;
};

const ComparisonRow = ({ label, value, tone, delta }) => (
  <li className="vlg-row" data-tone={tone}>
    <span className="vlg-row__label">{label}</span>
    <span className="vlg-row__value">{value}</span>
    {delta != null && <span className="vlg-row__delta" data-tone={tone}>{delta}</span>}
  </li>
);

const FullReportPhase = ({ fullReport, selectedBusiness, atRiskPillars, winningPillars, resetAll }) => {
  const tone = getScoreTone(fullReport.overall);
  const modules = fullReport.modules;

  // Competitive map (real positions): you = the searched business, rivals = the
  // competitors the scan already found. Coordinates only - no extra Google calls.
  const youCoord = selectedBusiness && selectedBusiness.lat && selectedBusiness.lng
    ? `${selectedBusiness.lat},${selectedBusiness.lng}`
    : null;
  const rivalCoords = (fullReport.competitors || [])
    .filter((c) => c && c.lat && c.lng)
    .slice(0, 12)
    .map((c) => `${c.lat},${c.lng}`);
  const competitiveMapUrl = (youCoord || rivalCoords.length)
    ? buildStaticMapUrl({ you: youCoord, rivals: rivalCoords })
    : null;

  return (
    <motion.div
      className="vlg-phase"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.36, ease: EASE }}
    >
      {fullReport.isDemo && (
        <div className="vlg-demo-badge" role="note">Sample result — illustrative demo, not a live scan</div>
      )}

      {/* Executive grade hero */}
      <div className="vlg-grade">
        <GradeRing score={fullReport.overall} letter={fullReport.letterGrade} tone={tone} />
        <div className="vlg-grade__body">
          <p className="vlg-grade__eyebrow">Full competitor report</p>
          <p className="vlg-grade__summary">
            You rank <b>#{fullReport.rank?.position} of {fullReport.rank?.total}</b> in your market.
          </p>
          <p className="vlg-grade__rankline">
            {ordinal(fullReport.percentile)} percentile{fullReport.tier ? ` · ${fullReport.tier}` : ''}
          </p>
        </div>
      </div>

      <div className="vlg-kpis">
        <div className="vlg-kpi">
          <div className="vlg-kpi__label">Overall grade</div>
          <div className="vlg-kpi__value">{fullReport.overall}/100 · {fullReport.letterGrade}</div>
        </div>
        <div className="vlg-kpi">
          <div className="vlg-kpi__label">Market rank</div>
          <div className="vlg-kpi__value">#{fullReport.rank?.position} of {fullReport.rank?.total}</div>
        </div>
        <div className="vlg-kpi">
          <div className="vlg-kpi__label">Gap to top quartile</div>
          <div className="vlg-kpi__value">+{fullReport.rank?.deltaToTopQuartile || 0} pts</div>
        </div>
        <div className="vlg-kpi">
          <div className="vlg-kpi__label">Gap to #1</div>
          <div className="vlg-kpi__value">+{fullReport.rank?.deltaToLeader || 0} pts</div>
        </div>
      </div>

      {/* Competitive map - real positions, you (gold) vs competitors (red) */}
      {competitiveMapUrl && (
        <div className="vlg-block">
          <p className="vlg-block__title" role="heading" aria-level="2">Your local competitive map</p>
          <figure className="vlg-repmap">
            <img
              className="vlg-repmap__img"
              src={competitiveMapUrl}
              alt="Map of your business and nearby competitors"
              loading="lazy"
              width="640"
              height="360"
            />
            <figcaption className="vlg-repmap__legend">
              <span><i className="vlg-repmap__dot vlg-repmap__dot--you" /> You</span>
              <span><i className="vlg-repmap__dot vlg-repmap__dot--rival" /> Competitors</span>
            </figcaption>
          </figure>
        </div>
      )}

      {/* League table */}
      {(fullReport.leagueTable || []).length > 0 && (
        <div className="vlg-block">
          <p className="vlg-block__title" role="heading" aria-level="2">League table - top competitors</p>
          <ul className="vlg-rows">
            {fullReport.leagueTable.slice(0, 6).map((competitor) => (
              <li className="vlg-row" data-tone={getScoreTone(competitor.finalScore)} key={competitor.id || competitor.name}>
                <span className="vlg-league__rank">#{competitor.rank}</span>
                <span className="vlg-league__name">{competitor.name}</span>
                <span className="vlg-league__score">{competitor.finalScore} · {competitor.percentile}th</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Pillar comparison */}
      <div className="vlg-block">
        <p className="vlg-block__title" role="heading" aria-level="2">You vs the top local cohort</p>

        {atRiskPillars.length > 0 && (
          <div className="vlg-group">
            <p className="vlg-group__title">At risk</p>
            <ul className="vlg-rows">
              {atRiskPillars.map((pillar) => (
                <ComparisonRow
                  key={pillar.key}
                  tone={pillar.tone}
                  label={pillar.label}
                  value={`${pillar.value}/100 vs ${pillar.hasCohort ? `${pillar.cohortValue}/100` : 'n/a'}`}
                  delta={pillar.hasCohort ? `${formatDelta(pillar.delta)} pts` : null}
                />
              ))}
            </ul>
          </div>
        )}

        {winningPillars.length > 0 && (
          <div className="vlg-group">
            <p className="vlg-group__title">Winning</p>
            <ul className="vlg-rows">
              {winningPillars.map((pillar) => (
                <ComparisonRow
                  key={pillar.key}
                  tone={pillar.tone}
                  label={pillar.label}
                  value={`${pillar.value}/100 vs ${pillar.hasCohort ? `${pillar.cohortValue}/100` : 'n/a'}`}
                  delta={pillar.hasCohort ? `${formatDelta(pillar.delta)} pts` : null}
                />
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Advanced module comparison */}
      {modules?.target && (
        <div className="vlg-block">
          <p className="vlg-block__title" role="heading" aria-level="2">Module comparison</p>
          <ul className="vlg-rows">
            <ComparisonRow
              tone={getScoreTone(modules.target.reviewSentiment?.score || 0)}
              label="Review sentiment"
              value={`${modules.target.reviewSentiment?.score || 0}/100 vs ${modules.competitorAverage?.reviewSentiment?.score ?? 'n/a'}/100`}
            />
            <ComparisonRow
              tone={getScoreTone(modules.target.photoQuality?.score || 0)}
              label="Photo quality"
              value={`${modules.target.photoQuality?.score || 0}/100 vs ${modules.competitorAverage?.photoQuality?.score ?? 'n/a'}/100`}
            />
            <ComparisonRow
              tone={getScoreTone(50)}
              label="Photo count"
              value={`${modules.target.photoQuality?.photoCount || 0} vs ${modules.competitorAverage?.photoQuality?.photoCount ?? 'n/a'}`}
            />
          </ul>
        </div>
      )}

      {/* Top opportunities */}
      {(fullReport.opportunities || []).length > 0 && (
        <div className="vlg-block">
          <p className="vlg-block__title" role="heading" aria-level="2">Top opportunities - impact × effort</p>
          <ul className="vlg-opps">
            {fullReport.opportunities.map((item) => {
              const it = getImpactTone(item.impact);
              return (
                <li className="vlg-opp" data-tone={it} key={item.title}>
                  <div className="vlg-opp__title">{item.title}</div>
                  <div className="vlg-chips">
                    <span className={`vlg-chip impact-${it}`}>{item.impact} impact</span>
                    <span className="vlg-chip">{item.effort} effort</span>
                  </div>
                  {item.detail && <p className="vlg-opp__detail">{item.detail}</p>}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Detailed module audit */}
      {Array.isArray(fullReport.moduleFindings) && fullReport.moduleFindings.length > 0 && (
        <div className="vlg-block">
          <p className="vlg-block__title" role="heading" aria-level="2">Detailed module audit</p>
          <ul className="vlg-modules">
            {fullReport.moduleFindings.map((module) => {
              const checks = Array.isArray(module?.checks) ? module.checks : [];
              const passed = checks.filter((item) => item?.pass).length;
              const failedChecks = getModuleFailedChecks(module);
              const detailLines = getModuleDetailLines(module);
              const score = Number(module.score || 0);
              const mTone = getScoreTone(score);
              return (
                <li className="vlg-module" data-tone={mTone} key={module.key || module.title}>
                  <div className="vlg-module__head">
                    <span className="vlg-module__title">{module.title || module.key}</span>
                    <span className="vlg-module__score">{module.score || 0}/100</span>
                  </div>
                  <p className="vlg-module__summary">
                    {module.ai?.summary || module.summary || 'In-depth checks completed for this section.'}
                  </p>
                  <div className="vlg-module__pts">
                    <span className="vlg-module__pt"><strong>Top risk:</strong> {module.ai?.topRisk || 'No major blockers detected.'}</span>
                    <span className="vlg-module__pt"><strong>Best action:</strong> {module.ai?.bestAction || 'Maintain performance and monitor monthly.'}</span>
                    <span className="vlg-module__pt"><strong>Checks passed:</strong> {passed}/{checks.length}</span>
                    {failedChecks.length > 0 && (
                      <span className="vlg-module__pt">
                        <strong>Top failed checks:</strong> {failedChecks.map((item) => item.label).join(' · ')}
                      </span>
                    )}
                    {detailLines.slice(0, 2).map((line) => (
                      <span className="vlg-module__pt" key={line}>{line}</span>
                    ))}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* AI strategic insight */}
      {fullReport.aiInsights?.summary && (
        <div className="vlg-block">
          <p className="vlg-block__title" role="heading" aria-level="2">AI strategic insight</p>
          <p className="vlg-ai__lead">{fullReport.aiInsights.summary}</p>
          {fullReport.aiInsights.competitiveNarrative && (
            <p className="vlg-ai__narr">{fullReport.aiInsights.competitiveNarrative}</p>
          )}

          {Array.isArray(fullReport.aiInsights.riskFlags) && fullReport.aiInsights.riskFlags.length > 0 && (
            <>
              <p className="vlg-block__title" style={{ marginTop: '18px' }} role="heading" aria-level="2">Risk flags</p>
              <div className="vlg-chips">
                {fullReport.aiInsights.riskFlags.slice(0, 6).map((risk) => (
                  <span key={risk} className="vlg-chip impact-critical">{risk}</span>
                ))}
              </div>
            </>
          )}

          {Array.isArray(fullReport.aiInsights.coaching) && fullReport.aiInsights.coaching.length > 0 && (
            <>
              <p className="vlg-block__title" style={{ marginTop: '18px' }} role="heading" aria-level="2">Coaching priorities</p>
              <div className="vlg-chips">
                {fullReport.aiInsights.coaching.slice(0, 4).map((tip) => (
                  <span key={tip} className="vlg-chip impact-warning">{tip}</span>
                ))}
              </div>
            </>
          )}

          {Array.isArray(fullReport.aiInsights.linkedActions) && fullReport.aiInsights.linkedActions.length > 0 && (
            <>
              <p className="vlg-block__title" style={{ marginTop: '18px' }} role="heading" aria-level="2">Linked actions</p>
              <div className="vlg-chips">
                {fullReport.aiInsights.linkedActions.slice(0, 6).map((action) => (
                  <span key={action} className="vlg-chip vlg-chip--gold">{action}</span>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Single CTA */}
      <div className="vlg-cta">
        <p className="vlg-cta__copy">
          This is your live competitive snapshot. Re-scan any time to track how you&apos;re closing the gap.
        </p>
        <button className="vlg-btn" type="button" onClick={resetAll}>Start a new scan</button>
      </div>

      <p className="vlg-disclaimer">
        Scores, grades, competitor figures and dollar estimates are derived from public data (including Google)
        and a general model — informational only, not a guarantee, and not an official rating of any business shown.
      </p>
    </motion.div>
  );
};

export default FullReportPhase;
